import React, { useEffect, useState } from 'react';

import { DateTime, Duration } from 'luxon';

import styled from '@emotion/styled';

import {
  AvailabilityFragment,
  MultidayAvailabilityFragment,
} from '@graphql/platform';

import type { SharedCheckoutDateScheduled } from '@root/components/checkout/data';

import { FROM_ISO_OPTIONS } from '@shared/calendar/calendar_utils';

import { formatCurrency } from '@utils/formatters';

import {
  CardPadded,
  HelpText,
  Question,
  SelectRow,
  SelectStack,
} from '../shared';
import { CHALLENGER as T } from '../tokens';

/** Flexible / all-day windows are ≥ 8 hours (same rule as classic ArrivalWindowSelector). */
const ALL_DAY_DURATION = Duration.fromObject({ hours: 8 });

type Availability = AvailabilityFragment | MultidayAvailabilityFragment;

/**
 * Prefer API `duration`; fall back to till − from so short windows still classify
 * as Scheduled when duration is omitted.
 */
const availabilityDuration = (
  availability: Pick<Availability, 'duration' | 'fromTime' | 'tillTime'>,
): Duration | undefined => {
  if (availability.duration) {
    return Duration.fromISO(availability.duration);
  }
  if (!availability.fromTime || !availability.tillTime) return undefined;
  const from = DateTime.fromISO(availability.fromTime, FROM_ISO_OPTIONS);
  const till = DateTime.fromISO(availability.tillTime, FROM_ISO_OPTIONS);
  if (!from.isValid || !till.isValid) return undefined;
  return till.diff(from);
};

const isFlexibleAvailability = (
  availability: Pick<Availability, 'duration' | 'fromTime' | 'tillTime'>,
): boolean => {
  const duration = availabilityDuration(availability);
  return !!duration && duration >= ALL_DAY_DURATION;
};

const isScheduledAvailability = (
  availability: Pick<Availability, 'duration' | 'fromTime' | 'tillTime'>,
): boolean => {
  const duration = availabilityDuration(availability);
  return !!duration && duration < ALL_DAY_DURATION;
};

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

const ScheduledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

/**
 * Figma 898:21873 — up to three equal chips in one row (gap 16).
 * Cap each chip at 1/3 width so a single window doesn’t stretch full-bleed
 * (classic used maxWidth 224px for the same reason).
 */
const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: ${T.space[16]};
  width: 100%;
`;

/** Three columns with 16px gaps → each chip ≤ (100% − 32px) / 3. */
const SLOT_MAX = 'calc((100% - 32px) / 3)';

const SlotButton = styled.button<{ selected?: boolean }>`
  appearance: none;
  cursor: pointer;
  flex: 1 1 ${SLOT_MAX};
  max-width: ${SLOT_MAX};
  min-width: min(140px, 100%);
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  padding: ${T.space[12]} ${T.space[16]};
  border-radius: ${T.radius.md};
  border: 1px solid
    ${({ selected }) =>
      selected ? T.color.borderBrand : T.color.borderDefault};
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  color: ${T.color.textPrimary};
  text-align: left;
  box-sizing: border-box;

  &:hover {
    border-color: ${T.color.borderBrand};
  }
`;

type Props = {
  selectedAvailability?: SharedCheckoutDateScheduled;
  availabilities: ReadonlyArray<Availability>;
  /** True once the customer has picked a calendar day. */
  dateSelected?: boolean;
  /**
   * Set a window, or pass `undefined` to clear the arrival choice (no auto
   * progression — customer must pick Flexible or a Scheduled chip).
   */
  onChange(availability: Availability | undefined, autoScroll: boolean): void;
};

/** Figma: `7AM - 10AM` (no minutes when :00, no space before AM/PM). */
const formatSlotClock = (value: DateTime): string => {
  const meridiem = value.toFormat('a').replace(/\s/g, '').toUpperCase();
  if (value.minute === 0) {
    return `${value.toFormat('h')}${meridiem}`;
  }
  return `${value.toFormat('h:mm')}${meridiem}`;
};

const formatSlotRange = (from: DateTime, till: DateTime): string =>
  `${formatSlotClock(from)} - ${formatSlotClock(till)}`;

/**
 * Figma Desktop 03/04 / node 707:10595 — Flexible + Scheduled + time chips.
 * Do not auto-select Flexible (or any window) when a calendar day is chosen.
 */
export const ChallengerArrivalOptions: React.FC<Props> = ({
  selectedAvailability,
  availabilities,
  dateSelected = false,
  onChange,
}) => {
  /**
   * Pending “Scheduled” UI before a time chip is chosen. Cleared when the
   * calendar day changes (component remounts via key) or Flexible is selected.
   * Do not clear when `selectedAvailability` becomes undefined — that happens
   * on the first Scheduled click after Flexible (clearing Flexible).
   */
  const [scheduledPending, setScheduledPending] = useState(false);

  useEffect(() => {
    if (!dateSelected) {
      setScheduledPending(false);
    }
  }, [dateSelected]);

  const flexibleWindow = availabilities.find(isFlexibleAvailability);
  const scheduledWindows = availabilities.filter(isScheduledAvailability);
  const scheduledFee =
    scheduledWindows.length > 0
      ? scheduledWindows[0].feeAmount?.value
      : undefined;

  const selectedFlexible = !!(
    !scheduledPending &&
    selectedAvailability &&
    isFlexibleAvailability(selectedAvailability)
  );
  const selectedScheduled = !!(
    scheduledPending ||
    (selectedAvailability && isScheduledAvailability(selectedAvailability))
  );

  const scheduledUnavailable =
    dateSelected && availabilities.length > 0 && scheduledWindows.length === 0;
  const scheduledDisabled =
    scheduledUnavailable || scheduledWindows.length === 0;

  /** Funnel-Feedback 6:699 — available copy; keep unavailable messaging for empty days. */
  const scheduledDescription = scheduledUnavailable
    ? 'Not available on this date. Choose a different pickup date to use this option.'
    : 'Choose the arrival window that works for your schedule. This is the ideal option if your building requires reservations or has restrictions.';

  /** Chips once Scheduled is chosen (pending or a slot already selected). */
  const showTimeSlots = selectedScheduled && scheduledWindows.length > 0;

  return (
    <CardPadded>
      <Options>
        <Header>
          <Question>What arrival window works for you?</Question>
          <HelpText>
            Our team will arrive anytime during the arrival window. Your pickup
            may continue beyond this window.
          </HelpText>
        </Header>
        <SelectStack>
          <SelectRow
            selected={selectedFlexible}
            onClick={() => {
              if (!flexibleWindow) return;
              setScheduledPending(false);
              onChange(flexibleWindow, false);
            }}
            title="Arrival between 7am - 3pm"
            description="We will notify you of your arrival window the day before."
            price="Included"
          />
          <ScheduledBlock>
            <SelectRow
              selected={selectedScheduled}
              disabled={scheduledDisabled}
              onClick={() => {
                if (scheduledDisabled) {
                  return;
                }
                // Expand chips only — do not auto-pick the first window.
                setScheduledPending(true);
                if (
                  selectedAvailability &&
                  isFlexibleAvailability(selectedAvailability)
                ) {
                  onChange(undefined, false);
                }
              }}
              title="Arrival during a specific window"
              description={scheduledDescription}
              price={
                scheduledFee != null
                  ? formatCurrency(scheduledFee, { maximumFractionDigits: 0 })
                  : '—'
              }
            />
            {showTimeSlots && (
              <SlotRow>
                {scheduledWindows.map((availability) => {
                  const selected =
                    !!selectedAvailability &&
                    availability.fromTime === selectedAvailability.fromTime &&
                    availability.tillTime === selectedAvailability.tillTime;
                  const from = DateTime.fromISO(
                    availability.fromTime,
                    FROM_ISO_OPTIONS,
                  );
                  const till = DateTime.fromISO(
                    availability.tillTime,
                    FROM_ISO_OPTIONS,
                  );
                  return (
                    <SlotButton
                      key={`${availability.fromTime}-${availability.tillTime}`}
                      type="button"
                      selected={selected}
                      onClick={() => {
                        setScheduledPending(true);
                        onChange(availability, false);
                      }}
                    >
                      {formatSlotRange(from, till)}
                    </SlotButton>
                  );
                })}
              </SlotRow>
            )}
          </ScheduledBlock>
        </SelectStack>
      </Options>
    </CardPadded>
  );
};
